import { Box, Button, Attribute } from "@/components/atoms";
import {
  Typography,
  Link,
  CircularProgress,
  Snackbar,
  Alert,
} from "@mui/material";
import { UnauthenticatedState } from "@/components/views";
import { User } from "@/types/user";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useResendConfirmationEmail } from "@/hooks";
import { useEffect, useState } from "react";
import { LabeledList } from "@/components/molecules";

dayjs.extend(relativeTime);

const InfoRow = ({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) => <Attribute componentName={label} label={label} value={value} />;

export const AboutSection = ({ user }: { user: User | null }) => {
  const { resendConfirmationEmail, data, loading, error } =
    useResendConfirmationEmail();
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success",
  );

  if (!user) return <UnauthenticatedState />;

  const handleResend = async () => {
    if (!user.email) return;
    await resendConfirmationEmail({ email: user.email });
  };

  useEffect(() => {
    if (data?.success) {
      setSnackbarMsg("Confirmation email sent!");
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
    } else if (error) {
      setSnackbarMsg(error);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [data, error]);

  const baseInfo = [
    { label: "First Name", value: user.first_name },
    { label: "Last Name", value: user.last_name },
    {
      label: "Email",
      value: (
        <>
          {user.email}{" "}
          <Typography
            component="span"
            variant="body2"
            sx={{
              color: user.is_email_confirmed ? "success.main" : "warning.main",
              fontWeight: "medium",
              ml: 1,
            }}
          >
            ({user.is_email_confirmed ? "confirmed" : "not confirmed"})
          </Typography>
          {!user.is_email_confirmed && (
            <Button
              componentName="resend-confirmation"
              onClick={handleResend}
              size="small"
              sx={{ ml: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={16} /> : "Resend confirmation"}
            </Button>
          )}
        </>
      ),
    },
    { label: "Membership", value: user.membership_type?.name || "None" },
  ];

  return (
    <Box
      componentName="about-section"
      border={false}
      sx={{
        m: 2,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbarSeverity}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
        >
          {snackbarMsg}
        </Alert>
      </Snackbar>

      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        About
      </Typography>

      {baseInfo.map(({ label, value }) => (
        <InfoRow key={label} label={label} value={value} />
      ))}

      <LabeledList
        label="Credentials"
        items={user.user_credentials?.map(c => c.credential.credential_name)}
      />

      <LabeledList
        label="Educations"
        items={user.user_educations?.map(e => e.education.education_name)}
      />

      <LabeledList
        label="Healthcare Domains"
        items={user.healthcare_domains?.map(e => e.healthcare_domain.value)}
      />

      <Typography variant="body2" sx={{ color: "text.secondary", mt: 2 }}>
        Joined: {dayjs(user.created_at).fromNow()}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Last Updated: {dayjs(user.updated_at).fromNow()}
      </Typography>

      <Typography variant="body2" sx={{ mt: 3 }}>
        To update these values, visit your{" "}
        <Link href="/account" underline="hover" color="primary">
          account page
        </Link>
        .
      </Typography>
    </Box>
  );
};
